import {
  Component, ChangeDetectorRef, OnInit, OnDestroy,
  ViewChild, ElementRef
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialogRef } from '@angular/material/dialog';
import { UmsService } from '../ums.service';
import { ModalpopService } from '../modalpop.service';
import { AlertBoxComponent } from '../alert-box/alert-box.component';
import { ConformationModelComponent } from '../conformation-model/conformation-model.component';
import * as XLSX from 'xlsx';

interface UploadLog {
  timestamp: Date;
  prnNo: string;
  result: boolean | null;
  message: string;
  details?: any;
  expanded?: boolean;
}

@Component({
  selector: 'app-marks-data-upload-sheets',
  templateUrl: './marks-data-upload-sheets.component.html',
  styleUrls: ['./marks-data-upload-sheets.component.scss']
})
export class MarksDataUploadSheetsComponent implements OnInit, OnDestroy {

  @ViewChild('barcodeLogsContainer', { static: false }) barcodeLogsContainer: ElementRef;
  @ViewChild('evaluationLogsContainer', { static: false }) evaluationLogsContainer: ElementRef;

  uploadForm: FormGroup;
  sessionList: any[] = [];
  SessionFilterList: any[] = [];
  CourseList: any[] = [];
  filteredListCourse: any[] = [];
  examList: any[] = [];
  filteredListExam: any[] = [];
  userPermission: any;
  dialogRef: MatDialogRef<any>;

  barcodeFile: File | null = null;
  barcodeFileName = '';
  isBarcodeUploading = false;
  barcodeProgress = 0;
  barcodeCurrentRecord = 0;
  barcodeTotalRecords = 0;
  barcodeSuccessCount = 0;
  barcodeFailCount = 0;
  barcodeEstimatedTime = '';
  barcodeStartTime = 0;
  barcodeFailedRecords: any[] = [];
  barcodeShowDownload = false;
  barcodeLogs: UploadLog[] = [];
  barcodeLogsSuccessCount = 0;
  barcodeLogsFailCount = 0;
  barcodeAutoScroll = true;

  evaluationFile: File | null = null;
  evaluationFileName = '';
  isEvaluationUploading = false;
  evaluationProgress = 0;
  evaluationCurrentRecord = 0;
  evaluationTotalRecords = 0;
  evaluationSuccessCount = 0;
  evaluationFailCount = 0;
  evaluationEstimatedTime = '';
  evaluationStartTime = 0;
  evaluationFailedRecords: any[] = [];
  evaluationShowDownload = false;
  evaluationLogs: UploadLog[] = [];
  evaluationLogsSuccessCount = 0;
  evaluationLogsFailCount = 0;
  evaluationAutoScroll = true;

  isProcessing = false;
  isFinalSubmitting = false;
  isReporting = false;

  private wakeLock: any = null;
  private wakeLockInterval: any = null;
  private preventSleepVideo: HTMLVideoElement | null = null;
  private beforeUnloadListener: any = null;

  private readonly BARCODE_COLUMNS = [
    'S_NO', 'P_NO', 'CLASS', 'COURSE', 'OLD', 'SUB_NO', 'UMS_SUB',
    'PRN_CODE', 'Subject', 'PAPER', 'Examdate', 'YR_HEADING',
    'PaperNo', 'Serialno', 'UpperRightBar', 'Msbar', 'BNO'
  ];

  private readonly BARCODE_REQUIRED = [
    'S_NO', 'P_NO', 'CLASS', 'COURSE', 'OLD', 'SUB_NO', 'UMS_SUB',
    'PRN_CODE', 'Subject', 'PAPER', 'Examdate', 'YR_HEADING',
    'PaperNo', 'UpperRightBar', 'Msbar', 'BNO'
  ];

  private readonly EVALUATION_COLUMNS = [
    'qp_code', 'usn', 'eval_level', 'marks',
    'usn_Final', 'exam_month'
  ];

  private readonly EVALUATION_REQUIRED = [
    'qp_code', 'usn', 'eval_level', 'marks'
  ];

  private readonly REPORT_COLUMNS = [
    'ExamsBarcodeId', 'CoCode', 'SnNo', 'EmId', 'StrId', 'P_NO',
    'EsId', 'UMS_SUB', 'Subject', 'PRN_NO', 'PaperNo', 'Examdate',
    'UpperRightBar', 'Eval1Marks', 'Eval2Marks', 'Eval3Marks', 'FinalMarks'
  ];

  constructor(
    private spinner: NgxSpinnerService,
    public umsService: UmsService,
    private fb: FormBuilder,
    private modalPopupService: ModalpopService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.userPermission = JSON.parse(sessionStorage.getItem('userPermission'));

    this.uploadForm = this.fb.group({
      SessionId: new FormControl('', Validators.required),
      course: new FormControl('', Validators.required),
      EmId: new FormControl('', Validators.required)
    });

    this.fetchSession();

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isAnyUploading()) {
        this.requestWakeLock();
      }
    });
  }

  fetchSession(): void {
    this.spinner.show();
    const payload = { SnDefault: '', SnNo: '' };

    this.umsService.getSession(payload).subscribe(
      (res: any) => {
        this.spinner.hide();
        this.sessionList = [];
        if (res.status) {
          res.data.forEach((element: any) => {
            if (element.SnStatus === 'O') this.sessionList.push(element);
          });
          this.SessionFilterList = this.sessionList.slice();
        }
      },
      () => {
        this.spinner.hide();
        this.sessionList = [];
        this.SessionFilterList = [];
      }
    );
  }

  getCourse(item: any): void {
    this.spinner.show();

    this.uploadForm.get('course').setValue('');
    this.uploadForm.get('EmId').setValue('');
    this.CourseList = [];
    this.filteredListCourse = [];
    this.examList = [];
    this.filteredListExam = [];

    this.umsService.getCourse({ SessionId: item.SnNo }).subscribe(
      (res: any) => {
        this.spinner.hide();
        if (res.status) {
          this.CourseList = res.data || [];
          this.filteredListCourse = this.CourseList.slice();
        }
      },
      () => {
        this.spinner.hide();
        this.CourseList = [];
        this.filteredListCourse = [];
      }
    );
  }

  getExamList(item: any): void {
    this.spinner.show();
    this.uploadForm.get('EmId').setValue('');
    this.examList = [];
    this.filteredListExam = [];

    this.umsService.getExam({ EmCoCode: item.CoCode }).subscribe(
      (res: any) => {
        this.spinner.hide();
        if (res.status) {
          this.examList = res.data || [];
          this.filteredListExam = this.examList.slice();
        } else {
          this.showError(res.message || 'Failed to fetch exams.');
        }
      },
      (ERROR: HttpErrorResponse) => {
        this.spinner.hide();
        this.showError(ERROR.error?.message || 'Failed to fetch exams.');
      }
    );
  }


  onBarcodeFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (!file) return;

    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      this.showError('Please select a valid Excel/CSV file (.xlsx, .xls, .csv).');
      event.target.value = '';
      return;
    }

    this.barcodeFile = file;
    this.barcodeFileName = file.name;

    this.barcodeTotalRecords = 0;
    this.barcodeSuccessCount = 0;
    this.barcodeFailCount = 0;
    this.barcodeProgress = 0;
    this.barcodeCurrentRecord = 0;
    this.barcodeEstimatedTime = '';
    this.barcodeShowDownload = false;
    this.barcodeFailedRecords = [];
  }

  onEvaluationFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (!file) return;

    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      this.showError('Please select a valid Excel/CSV file (.xlsx, .xls, .csv).');
      event.target.value = '';
      return;
    }

    this.evaluationFile = file;
    this.evaluationFileName = file.name;

    this.evaluationTotalRecords = 0;
    this.evaluationSuccessCount = 0;
    this.evaluationFailCount = 0;
    this.evaluationProgress = 0;
    this.evaluationCurrentRecord = 0;
    this.evaluationEstimatedTime = '';
    this.evaluationShowDownload = false;
    this.evaluationFailedRecords = [];
  }


  uploadBarcodeData(): void {
    if (!this.validateDropdowns()) return;

    if (!this.barcodeFile) {
      this.showError('Please choose an Excel file first.');
      return;
    }

    this.processBarcodeFile(this.barcodeFile);
  }

  uploadEvaluationData(): void {
    if (!this.validateDropdowns()) return;

    if (!this.evaluationFile) {
      this.showError('Please choose an Excel file first.');
      return;
    }

    this.processEvaluationFile(this.evaluationFile);
  }


  isActionReady(): boolean {
    const v = this.uploadForm.value;
    return !!(v.SessionId && v.course && v.EmId);
  }

  private buildActionPayload(): any {
    const formValue = this.uploadForm.value;
    const userId = parseInt(sessionStorage.getItem('userId'), 10) || null;

    return {
      CoCode: formValue.course,
      SnNo: formValue.SessionId,
      EmId: formValue.EmId,
      UserId: userId
    };
  }

  processData(): void {
    if (!this.isActionReady()) {
      this.showError('Please select Session, Course and Exam first.');
      return;
    }

    const confirmData: any = {
      message: 'Are you sure you want to process the final exam marks?',
      title: 'Confirmation',
      icon: 'fa fa-question'
    };

    this.dialogRef = this.modalPopupService.openPopup<ConformationModelComponent>(
      ConformationModelComponent, confirmData
    );

    this.dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== 'yes') return;

      this.isProcessing = true;
      this.spinner.show();

      const payload = this.buildActionPayload();
      this.umsService.validateExamsFinalMarks(payload).subscribe(
        (validateRes: any) => {
          const isValid = validateRes && validateRes.status === true;

          if (isValid) {
            this.callProcessApi(payload);
          } else {
            this.spinner.hide();
            this.isProcessing = false;

            const baseMsg = validateRes?.message || 'Validation failed.';

            let fullMsg = baseMsg;
            if (Array.isArray(validateRes?.data) && validateRes.data.length > 0) {
              const issues = validateRes.data
                .map((d: any) => d.Detail || d.Detail || d.Message || '')
                .filter((m: string) => m && m.trim() !== '')
                .join('\n');

              if (issues) {
                fullMsg = `${baseMsg}\n\n${issues}`;
              }
            }

            this.showError(fullMsg);
          }
        },
        (err: HttpErrorResponse) => {
          this.spinner.hide();
          this.isProcessing = false;

          const serverMsg =
            err?.error?.message ||
            err?.error?.Message ||
            err?.message ||
            'Validation failed.';

          this.showError(serverMsg);
        }
      );
    });
  }
  private callProcessApi(payload: any): void {
    this.umsService.processExamsFinalMarks(payload).subscribe(
      (res: any) => {
        this.spinner.hide();
        this.isProcessing = false;

        const serverMsg =
          res?.message ||
          res?.Message ||
          res?.data?.message ||
          '';

        if (res && res.status) {
          this.showSuccess(serverMsg || 'Processed successfully.');
        } else {
          this.showError(serverMsg || 'Processing failed.');
        }
      },
      (err: HttpErrorResponse) => {
        this.spinner.hide();
        this.isProcessing = false;

        const serverMsg =
          err?.error?.message ||
          err?.error?.Message ||
          err?.message ||
          '';

        this.showError(serverMsg || 'Processing failed.');
      }
    );
  }

  downloadReport(): void {
  if (!this.isActionReady()) {
    this.showError('Please select Session, Course and Exam first.');
    return;
  }

  this.isReporting = true;
  this.spinner.show();

  const payload = {
    ...this.buildActionPayload(),
    P_NO: null,
    IsFinal: null,
    IsZero: 0,
  };

  this.umsService.getExamsFinalMarksReport(payload).subscribe(
    (res: any) => {
      this.spinner.hide();
      this.isReporting = false;

      if (!res || !res.status || !res.data || res.data.length === 0) {
        this.showError(res?.message || 'No data found for the report.');
        return;
      }

      const columns: { key: string; header: string }[] = [
        { key: 'CoName',     header: 'Course Name'  },
        { key: 'S_NO',     header: 'Seat Number'  },
        { key: 'SnName',     header: 'Session Name' },
        { key: 'EmName',     header: 'Exam Name'    },
        { key: 'Sname',      header: 'Subject'      },
        { key: 'PaperNo',    header: 'Paper Name'   },
        { key: 'Hname',      header: 'Head Name'    },
        { key: 'Msbar',      header: 'MsBar'        },
        { key: 'Eval1Marks', header: 'Evaluation 1' },
        { key: 'Eval2Marks', header: 'Evaluation 2' },
        { key: 'Eval3Marks', header: 'Evaluation 3' },
        { key: 'FinalMarks', header: 'Final Marks'  }
      ];

      const headers = columns.map(c => c.header);

      // Skip the trailing summary object (TotalBarcodes / NotProcessed / ...)
      const filtered = res.data
        .filter((row: any) => row && row.ExamsBarcodeId !== undefined)
        .map((row: any) => {
          const picked: any = {};
          columns.forEach(({ key, header }) => {
            const val = row[key];
            // Keep 0 as 0, only null/undefined become blanks
            picked[header] = val !== undefined && val !== null ? val : '';
          });
          return picked;
        });

      if (filtered.length === 0) {
        this.showError('No report rows found.');
        return;
      }

      const ws = XLSX.utils.json_to_sheet(filtered, { header: headers });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ExamsFinalMarks');

      const v = this.uploadForm.value;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `Marks_EvaluationReport_${v.SessionId}_${v.course}_${v.EmId}_${ts}.xlsx`;

      XLSX.writeFile(wb, fileName);
      this.showSuccess('Report downloaded successfully.');
    },
    (err: HttpErrorResponse) => {
      this.spinner.hide();
      this.isReporting = false;
      this.showError(
        err?.error?.message ||
        err?.error?.Message ||
        err?.message ||
        'Failed to fetch the report.'
      );
    }
  );
}
  downloadReport1(): void {
  if (!this.isActionReady()) {
    this.showError('Please select Session, Course and Exam first.');
    return;
  }

  this.isReporting = true;
  this.spinner.show();

  const payload = {
    ...this.buildActionPayload(),
    P_NO: null,
    IsFinal: null,
    IsZero: 1,
  };

  this.umsService.getExamsFinalMarksReport(payload).subscribe(
    (res: any) => {
      this.spinner.hide();
      this.isReporting = false;

      if (!res || !res.status || !res.data || res.data.length === 0) {
        this.showError(res?.message || 'No data found for the report.');
        return;
      }

      const columns: { key: string; header: string }[] = [
        { key: 'CoName',     header: 'Course Name'  },
        { key: 'S_NO',     header: 'Seat Number'  },
        { key: 'SnName',     header: 'Session Name' },
        { key: 'EmName',     header: 'Exam Name'    },
        { key: 'Sname',      header: 'Subject'      },
        { key: 'PaperNo',    header: 'Paper Name'   },
        { key: 'Hname',      header: 'Head Name'    },
        { key: 'Msbar',      header: 'MsBar'        },
        { key: 'Eval1Marks', header: 'Evaluation 1' },
        { key: 'Eval2Marks', header: 'Evaluation 2' },
        { key: 'Eval3Marks', header: 'Evaluation 3' },
        { key: 'FinalMarks', header: 'Final Marks'  }
      ];

      const headers = columns.map(c => c.header);

      // Skip the trailing summary object (TotalBarcodes / NotProcessed / ...)
      const filtered = res.data
        .filter((row: any) => row && row.ExamsBarcodeId !== undefined)
        .map((row: any) => {
          const picked: any = {};
          columns.forEach(({ key, header }) => {
            const val = row[key];
            // Keep 0 as 0, only null/undefined become blanks
            picked[header] = val !== undefined && val !== null ? val : '';
          });
          return picked;
        });

      if (filtered.length === 0) {
        this.showError('No report rows found.');
        return;
      }

      const ws = XLSX.utils.json_to_sheet(filtered, { header: headers });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ExamsFinalMarks');

      const v = this.uploadForm.value;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `Zero_MarksReport_${v.SessionId}_${v.course}_${v.EmId}_${ts}.xlsx`;

      XLSX.writeFile(wb, fileName);
      this.showSuccess('Report downloaded successfully.');
    },
    (err: HttpErrorResponse) => {
      this.spinner.hide();
      this.isReporting = false;
      this.showError(
        err?.error?.message ||
        err?.error?.Message ||
        err?.message ||
        'Failed to fetch the report.'
      );
    }
  );
}
  finalSubmit(): void {
    if (!this.isActionReady()) {
      this.showError('Please select Session, Course and Exam first.');
      return;
    }

    const confirmData: any = {
      message: 'Are you sure you want to submit the final exam marks? ' +
               'You will not be able to make any changes afterward.',
      title: 'Confirm Final Submit',
      icon: 'fa fa-question'
    };

    this.dialogRef = this.modalPopupService.openPopup<ConformationModelComponent>(
      ConformationModelComponent, confirmData
    );

    this.dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== 'yes') return;

      this.isFinalSubmitting = true;
      this.spinner.show();

      const payload = this.buildActionPayload();

      this.umsService.submitExamsFinalMarks(payload).subscribe(
        (res: any) => {
          this.spinner.hide();
          this.isFinalSubmitting = false;

          const serverMsg =
            res?.message ||
            res?.Message ||
            res?.data?.message ||
            '';

          if (res && res.status) {
            this.showSuccess(serverMsg || 'Final submission completed.');
          } else {
            this.showError(serverMsg || 'Final submission failed.');
          }
        },
        (err: HttpErrorResponse) => {
          this.spinner.hide();
          this.isFinalSubmitting = false;

          const serverMsg =
            err?.error?.message ||
            err?.error?.Message ||
            err?.message ||
            '';

          this.showError(serverMsg || 'Final submission failed.');
        }
      );
    });
  }


  private processBarcodeFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const raw: any[] = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

      if (!raw || raw.length === 0) {
        this.showError('The uploaded Excel sheet is empty.');
        return;
      }

      const cleaned = raw.map(row => this.cleanRow(row));
      const normalised = cleaned.map(row => this.normaliseBarcodeRow(row));

      const missing = this.BARCODE_REQUIRED.filter(col =>
        !Object.prototype.hasOwnProperty.call(normalised[0], col) ||
        normalised[0][col] === undefined
      );
      if (missing.length > 0) {
        this.showError(
          `Missing column(s) in the Excel: ${missing.join(', ')}. Please use the correct template.`
        );
        this.resetBarcodeFile();
        return;
      }

      this.validateAndUploadBarcode(normalised);
    };

    reader.onerror = () => this.showError('Failed to read the Excel file.');
    reader.readAsArrayBuffer(file);
  }

  private async validateAndUploadBarcode(records: any[]): Promise<void> {
    this.barcodeFailedRecords = [];
    this.barcodeShowDownload = false;
    this.barcodeSuccessCount = 0;
    this.barcodeFailCount = 0;
    this.barcodeTotalRecords = records.length;
    this.barcodeProgress = 0;
    this.barcodeCurrentRecord = 0;
    this.barcodeLogs = [];
    this.barcodeLogsSuccessCount = 0;
    this.barcodeLogsFailCount = 0;

    this.addBarcodeLog({
      timestamp: new Date(),
      prnNo: '',
      result: null,
      message: `Starting barcode upload of ${records.length} record(s)...`
    });

    const validRecords = records.filter((record) => {
      const err = this.validateBarcodeRecord(record);
      if (err) {
        record['Result'] = 'false';
        record['Message'] = err;
        this.barcodeFailedRecords.push(record);

        this.addBarcodeLog({
          timestamp: new Date(),
          prnNo: record.PRN_CODE || 'Unknown',
          result: false,
          message: `Validation Failed: ${err}`,
          details: record
        });
        return false;
      }
      return true;
    });

    if (this.barcodeFailedRecords.length > 0) {
      this.barcodeFailCount = this.barcodeFailedRecords.length;

      this.addBarcodeLog({
        timestamp: new Date(),
        prnNo: '',
        result: false,
        message: `${this.barcodeFailedRecords.length} record(s) failed validation. Please fix and re-upload.`
      });

      this.showError('Some records are invalid. Please fix them and re-upload.');
      this.barcodeShowDownload = true;
      return;
    }

    await this.requestWakeLock();

    this.isBarcodeUploading = true;
    this.barcodeStartTime = Date.now();

    const formValue = this.uploadForm.value;
    const userId = parseInt(sessionStorage.getItem('userId'), 10) || 0;

    for (let count = 0; count < validRecords.length; count++) {
      const record = validRecords[count];

      this.barcodeCurrentRecord = count + 1;
      this.barcodeProgress = Math.round(((count + 1) / validRecords.length) * 100);

      if (count > 2) {
        this.barcodeEstimatedTime = this.computeEta(
          this.barcodeStartTime, count + 1, validRecords.length
        );
      } else {
        this.barcodeEstimatedTime = 'Calculating...';
      }

      const examDate =
        record.Examdate && String(record.Examdate).trim() !== ''
          ? record.Examdate
          : null;

      const payload = {
        CoCode: formValue.course,
        SnNo: formValue.SessionId,
        EmId: formValue.EmId,
        UserId: userId,
        S_NO: record.S_NO,
        P_NO: record.P_NO,
        CLASS: record.CLASS,
        COURSE: record.COURSE,
        OLD: record.OLD,
        SUB_NO: record.SUB_NO,
        UMS_SUB: record.UMS_SUB,
        PRN_CODE: record.PRN_CODE,
        Subject: record.Subject,
        PAPER: record.PAPER,
        Examdate: examDate,
        YR_HEADING: record.YR_HEADING,
        PaperNo: record.PaperNo,
        Serialno: record.Serialno,
        UpperRightBar: record.UpperRightBar,
        Msbar: record.Msbar,
        BNO: record.BNO
      };

      try {
        const res: any = await this.callBarcodeApi(payload);
        const isSuccess = res && (res.Result === 1 || res.Result === true);

        if (!isSuccess) {
          this.barcodeFailCount++;
          record['Result'] = 'false';
          record['Message'] = res?.Message || 'Backend validation failed';
          this.barcodeFailedRecords.push(record);

          this.addBarcodeLog({
            timestamp: new Date(),
            prnNo: record.PRN_CODE || 'Unknown',
            result: false,
            message: res?.Message || 'Backend validation failed',
            details: record
          });
        } else {
          this.barcodeSuccessCount++;
          this.addBarcodeLog({
            timestamp: new Date(),
            prnNo: record.PRN_CODE || 'Unknown',
            result: true,
            message: res.Message || 'Record imported successfully',
            details: record
          });
        }
      } catch (err) {
        const error = err as any;
        this.barcodeFailCount++;
        const msg = error?.message || 'API Error - server unreachable or network issue';

        record['Result'] = 'false';
        record['Message'] = msg;
        this.barcodeFailedRecords.push(record);

        this.addBarcodeLog({
          timestamp: new Date(),
          prnNo: record.PRN_CODE || 'Unknown',
          result: false,
          message: msg,
          details: error?.httpError?.error ?? error
        });
      }

      if (count % 5 === 0) {
        this.cdr.detectChanges();
        this.scrollLogsToTop('barcode');
      }
      if (count % 50 === 0 && count > 0) {
        await this.delay(10);
      }
    }

    this.barcodeFailCount = this.barcodeFailedRecords.length;
    this.isBarcodeUploading = false;

    this.addBarcodeLog({
      timestamp: new Date(),
      prnNo: '',
      result: this.barcodeFailCount === 0 ? true : null,
      message: `Barcode upload completed! Success: ${this.barcodeSuccessCount}, Failed: ${this.barcodeFailCount}`
    });

    this.cdr.detectChanges();
    this.barcodeShowDownload = this.barcodeFailedRecords.length > 0;

    if (this.barcodeShowDownload) {

      const lastFailMessage =
        this.barcodeLogs.find(l => l.result === false && l.message)?.message || '';
      const allFailedWithSameMessage =
        this.barcodeFailCount === this.barcodeTotalRecords &&
        lastFailMessage;

      this.showError(
        allFailedWithSameMessage
          ? lastFailMessage
          : `Barcode upload finished with errors. Success: ${this.barcodeSuccessCount}, Failed: ${this.barcodeFailCount}.`
      );
    } else {
      this.showSuccess(`All ${this.barcodeSuccessCount} barcode record(s) uploaded successfully.`);
    }

    await this.releaseWakeLock();
  }

  private callBarcodeApi(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.umsService.importExamsBarcode(payload).subscribe({
        next: (res: any) => resolve(res),
        error: (err: HttpErrorResponse) => {
          const serverMsg =
            err?.error?.Message ||
            err?.error?.message ||
            err?.message ||
            'API Error - server unreachable or network issue';
          reject({ httpError: err, message: serverMsg });
        }
      });
    });
  }

  private validateBarcodeRecord(record: any): string | null {
    for (const field of this.BARCODE_REQUIRED) {
      if (!record[field] || String(record[field]).trim() === '') {
        return `${field} is required`;
      }
    }
    return null;
  }

  private processEvaluationFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const raw: any[] = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

      if (!raw || raw.length === 0) {
        this.showError('The uploaded Excel sheet is empty.');
        return;
      }

      const cleaned = raw.map(row => this.cleanRow(row));
      const normalised = cleaned.map(row => this.normaliseEvaluationRow(row));

      const missing = this.EVALUATION_REQUIRED.filter(col =>
        !Object.prototype.hasOwnProperty.call(normalised[0], col) ||
        normalised[0][col] === undefined
      );
      if (missing.length > 0) {
        this.showError(
          `Missing column(s) in the Excel: ${missing.join(', ')}. Please use the correct template.`
        );
        this.resetEvaluationFile();
        return;
      }

      this.validateAndUploadEvaluation(normalised);
    };

    reader.onerror = () => this.showError('Failed to read the Excel file.');
    reader.readAsArrayBuffer(file);
  }

  private async validateAndUploadEvaluation(records: any[]): Promise<void> {
    this.evaluationFailedRecords = [];
    this.evaluationShowDownload = false;
    this.evaluationSuccessCount = 0;
    this.evaluationFailCount = 0;
    this.evaluationTotalRecords = records.length;
    this.evaluationProgress = 0;
    this.evaluationCurrentRecord = 0;
    this.evaluationLogs = [];
    this.evaluationLogsSuccessCount = 0;
    this.evaluationLogsFailCount = 0;

    this.addEvaluationLog({
      timestamp: new Date(),
      prnNo: '',
      result: null,
      message: `Starting evaluation upload of ${records.length} record(s)...`
    });

    const validRecords = records.filter((record) => {
      const err = this.validateEvaluationRecord(record);
      if (err) {
        record['Result'] = 'false';
        record['Message'] = err;
        this.evaluationFailedRecords.push(record);

        this.addEvaluationLog({
          timestamp: new Date(),
          prnNo: record.usn || 'Unknown',
          result: false,
          message: `Validation Failed: ${err}`,
          details: record
        });
        return false;
      }
      return true;
    });

    if (this.evaluationFailedRecords.length > 0) {
      this.evaluationFailCount = this.evaluationFailedRecords.length;

      this.addEvaluationLog({
        timestamp: new Date(),
        prnNo: '',
        result: false,
        message: `${this.evaluationFailedRecords.length} record(s) failed validation. Please fix and re-upload.`
      });

      this.showError('Some records are invalid. Please fix them and re-upload.');
      this.evaluationShowDownload = true;
      return;
    }

    await this.requestWakeLock();

    this.isEvaluationUploading = true;
    this.evaluationStartTime = Date.now();

    const formValue = this.uploadForm.value;
    const userId = parseInt(sessionStorage.getItem('userId'), 10) || 0;

    for (let count = 0; count < validRecords.length; count++) {
      const record = validRecords[count];

      this.evaluationCurrentRecord = count + 1;
      this.evaluationProgress = Math.round(((count + 1) / validRecords.length) * 100);

      if (count > 2) {
        this.evaluationEstimatedTime = this.computeEta(
          this.evaluationStartTime, count + 1, validRecords.length
        );
      } else {
        this.evaluationEstimatedTime = 'Calculating...';
      }

      const payload = {
        CoCode: formValue.course,
        SnNo: formValue.SessionId,
        EmId: formValue.EmId,
        UserId: userId,
        qp_code: record.qp_code,
        usn: record.usn,
        eval_level: record.eval_level,
        marks: record.marks,
        usn_Final: record.usn_Final || null,
        exam_month: record.exam_month || null
      };

      try {
        const res: any = await this.callEvaluationApi(payload);
        const isSuccess = res && (res.Result === 1 || res.Result === true);

        if (!isSuccess) {
          this.evaluationFailCount++;
          record['Result'] = 'false';
          record['Message'] = res?.Message || 'Backend validation failed';
          this.evaluationFailedRecords.push(record);

          this.addEvaluationLog({
            timestamp: new Date(),
            prnNo: record.usn || 'Unknown',
            result: false,
            message: res?.Message || 'Backend validation failed',
            details: record
          });
        } else {
          this.evaluationSuccessCount++;
          this.addEvaluationLog({
            timestamp: new Date(),
            prnNo: record.usn || 'Unknown',
            result: true,
            message: res.Message || 'Record imported successfully',
            details: record
          });
        }
      } catch (err) {
        const error = err as any;
        this.evaluationFailCount++;
        const msg = error?.message || 'API Error - server unreachable or network issue';

        record['Result'] = 'false';
        record['Message'] = msg;
        this.evaluationFailedRecords.push(record);

        this.addEvaluationLog({
          timestamp: new Date(),
          prnNo: record.usn || 'Unknown',
          result: false,
          message: msg,
          details: error?.httpError?.error ?? error
        });
      }

      if (count % 5 === 0) {
        this.cdr.detectChanges();
        this.scrollLogsToTop('evaluation');
      }
      if (count % 50 === 0 && count > 0) {
        await this.delay(10);
      }
    }

    this.evaluationFailCount = this.evaluationFailedRecords.length;
    this.isEvaluationUploading = false;

    this.addEvaluationLog({
      timestamp: new Date(),
      prnNo: '',
      result: this.evaluationFailCount === 0 ? true : null,
      message: `Evaluation upload completed! Success: ${this.evaluationSuccessCount}, Failed: ${this.evaluationFailCount}`
    });

    this.cdr.detectChanges();
    this.evaluationShowDownload = this.evaluationFailedRecords.length > 0;

    if (this.evaluationShowDownload) {
      const lastFailMessage =
        this.evaluationLogs.find(l => l.result === false && l.message)?.message || '';

      const allFailedWithSameMessage =
        this.evaluationFailCount === this.evaluationTotalRecords &&
        lastFailMessage;

      this.showError(
        allFailedWithSameMessage
          ? lastFailMessage
          : `Evaluation upload finished with errors. Success: ${this.evaluationSuccessCount}, Failed: ${this.evaluationFailCount}.`
      );
    } else {
      this.showSuccess(`All ${this.evaluationSuccessCount} evaluation record(s) uploaded successfully.`);
    }

    await this.releaseWakeLock();
  }

  private callEvaluationApi(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.umsService.importEvaluationData(payload).subscribe({
        next: (res: any) => resolve(res),
        error: (err: HttpErrorResponse) => {
          const serverMsg =
            err?.error?.Message ||
            err?.error?.message ||
            err?.message ||
            'API Error - server unreachable or network issue';
          reject({ httpError: err, message: serverMsg });
        }
      });
    });
  }

  private validateEvaluationRecord(record: any): string | null {
    for (const field of this.EVALUATION_REQUIRED) {
      if (!record[field] || String(record[field]).trim() === '') {
        return `${field} is required`;
      }
    }

    if (isNaN(Number(record.marks))) {
      return 'marks must be a number';
    }

    return null;
  }



  private cleanRow(row: any): any {
    const cleaned: any = {};

    for (const key in row) {
      if (!row.hasOwnProperty(key)) continue;

      const raw = row[key];

      if (raw instanceof Date && !isNaN(raw.getTime())) {
        const yyyy = raw.getFullYear();
        const mm = String(raw.getMonth() + 1).padStart(2, '0');
        const dd = String(raw.getDate()).padStart(2, '0');
        cleaned[key.trim()] = `${yyyy}-${mm}-${dd}`;
        continue;
      }

      let value = String(raw ?? '');
      value = value.replace(/[\u200B-\u200D\uFEFF]/g, '');
      value = value.replace(/[^a-zA-Z0-9@._/,\- ]/g, '').replace(/\s+/g, ' ').trim();
      cleaned[key.trim()] = value;
    }

    return cleaned;
  }

  private normaliseBarcodeRow(row: any): any {
    const out: any = {};
    const lookup = (name: string): any => {
      const target = name.replace(/[_\s]/g, '').toLowerCase();
      const key = Object.keys(row).find(
        k => k.replace(/[_\s]/g, '').toLowerCase() === target
      );
      return key !== undefined ? row[key] : '';
    };
    this.BARCODE_COLUMNS.forEach(col => { out[col] = lookup(col); });
    return out;
  }

  private normaliseEvaluationRow(row: any): any {
    const out: any = {};
    const lookup = (name: string): any => {
      const target = name.replace(/[_\s]/g, '').toLowerCase();
      const key = Object.keys(row).find(
        k => k.replace(/[_\s]/g, '').toLowerCase() === target
      );
      return key !== undefined ? row[key] : '';
    };
    this.EVALUATION_COLUMNS.forEach(col => { out[col] = lookup(col); });
    return out;
  }


  private addBarcodeLog(log: UploadLog): void {
    this.barcodeLogs.unshift(log);
    if (log.result === true) this.barcodeLogsSuccessCount++;
    else if (log.result === false) this.barcodeLogsFailCount++;

    if (this.barcodeLogs.length > 500) {
      this.barcodeLogs = this.barcodeLogs.slice(0, 500);
    }
    if (this.barcodeAutoScroll) {
      setTimeout(() => this.scrollLogsToTop('barcode'), 50);
    }
  }

  private addEvaluationLog(log: UploadLog): void {
    this.evaluationLogs.unshift(log);
    if (log.result === true) this.evaluationLogsSuccessCount++;
    else if (log.result === false) this.evaluationLogsFailCount++;

    if (this.evaluationLogs.length > 500) {
      this.evaluationLogs = this.evaluationLogs.slice(0, 500);
    }
    if (this.evaluationAutoScroll) {
      setTimeout(() => this.scrollLogsToTop('evaluation'), 50);
    }
  }

  scrollLogsToTop(which: 'barcode' | 'evaluation'): void {
    const ref = which === 'barcode' ? this.barcodeLogsContainer : this.evaluationLogsContainer;
    const auto = which === 'barcode' ? this.barcodeAutoScroll : this.evaluationAutoScroll;
    if (ref && auto) {
      try { ref.nativeElement.scrollTop = 0; } catch { /* noop */ }
    }
  }

  toggleLogDetails(which: 'barcode' | 'evaluation', index: number): void {
    const arr = which === 'barcode' ? this.barcodeLogs : this.evaluationLogs;
    if (arr[index]) arr[index].expanded = !arr[index].expanded;
  }

  clearLogs(which: 'barcode' | 'evaluation'): void {
    if (!confirm('Are you sure you want to clear all logs?')) return;
    if (which === 'barcode') {
      this.barcodeLogs = [];
      this.barcodeLogsSuccessCount = 0;
      this.barcodeLogsFailCount = 0;
    } else {
      this.evaluationLogs = [];
      this.evaluationLogsSuccessCount = 0;
      this.evaluationLogsFailCount = 0;
    }
  }


  downloadFailedExcel(which: 'barcode' | 'evaluation'): void {
    const rows = which === 'barcode' ? this.barcodeFailedRecords : this.evaluationFailedRecords;
    if (rows.length === 0) {
      this.showError('No failed records to download.');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'FailedRecords');
    XLSX.writeFile(
      wb,
      which === 'barcode' ? 'Barcode_Failed_Records.xlsx' : 'Evaluation_Failed_Records.xlsx'
    );
  }



  private isAnyUploading(): boolean {
    return this.isBarcodeUploading || this.isEvaluationUploading;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private computeEta(startTime: number, current: number, total: number): string {
    const elapsed = Date.now() - startTime;
    const avg = elapsed / current;
    const remaining = (total - current) * avg;

    if (remaining < 1000) return 'Less than 1s';
    const h = Math.floor(remaining / 3600000);
    const m = Math.floor((remaining % 3600000) / 60000);
    const s = Math.floor((remaining % 60000) / 1000);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }

  async requestWakeLock(): Promise<void> {
    try {
      if ('wakeLock' in navigator) {
        this.wakeLock = await (navigator as any).wakeLock.request('screen');
        this.wakeLock.addEventListener('release', () => {
          if (this.isAnyUploading()) {
            setTimeout(() => this.requestWakeLock(), 1000);
          }
        });
      }
    } catch { /* silent */ }

    this.createPreventSleepVideo();
    this.startWakeLockInterval();
    this.preventPageUnload();
  }

  private createPreventSleepVideo(): void {
    if (this.preventSleepVideo) return;
    this.preventSleepVideo = document.createElement('video');
    this.preventSleepVideo.setAttribute('muted', '');
    this.preventSleepVideo.setAttribute('playsinline', '');
    this.preventSleepVideo.setAttribute('loop', '');
    this.preventSleepVideo.style.position = 'fixed';
    this.preventSleepVideo.style.opacity = '0';
    this.preventSleepVideo.style.width = '1px';
    this.preventSleepVideo.style.height = '1px';
    this.preventSleepVideo.style.pointerEvents = 'none';
    this.preventSleepVideo.src =
      'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAs1tZGF0AAACrgYF//+q3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1NSByMjkwMSA3ZDBmZjIyIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxOCAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEwIHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAA+WIiIAAX//72rvzLK0cLlS4dWXuzUfLoSXL9iDB9aAAAAwAAAwAAJuKiZ0WFMeJsgAAALmAIWElDyDzETFWKgStAAAADAAADAAADAAADAAAHgvugkks0lyOD2SZ95BAAAADAQY2=';
    document.body.appendChild(this.preventSleepVideo);
    this.preventSleepVideo.play().catch(() => { /* silent */ });
  }

  private startWakeLockInterval(): void {
    if (this.wakeLockInterval) return;
    this.wakeLockInterval = setInterval(() => {
      if (this.isAnyUploading()) {
        window.scrollBy(0, 1);
        window.scrollBy(0, -1);
        requestAnimationFrame(() => { /* keep active */ });
      }
    }, 10000);
  }

  private preventPageUnload(): void {
    if (this.beforeUnloadListener) return;
    this.beforeUnloadListener = (e: BeforeUnloadEvent) => {
      if (this.isAnyUploading()) {
        const msg = 'Upload in progress! Are you sure you want to leave? All progress will be lost.';
        e.preventDefault();
        e.returnValue = msg;
        return msg;
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }

  async releaseWakeLock(): Promise<void> {
    if (this.wakeLock) {
      try { await this.wakeLock.release(); } catch { /* silent */ }
      this.wakeLock = null;
    }
    if (this.preventSleepVideo) {
      this.preventSleepVideo.pause();
      this.preventSleepVideo.remove();
      this.preventSleepVideo = null;
    }
    if (this.wakeLockInterval) {
      clearInterval(this.wakeLockInterval);
      this.wakeLockInterval = null;
    }
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }
  }

  private validateDropdowns(): boolean {
    if (this.uploadForm.invalid) {
      this.uploadForm.markAllAsTouched();
      this.showError('Please select Session, Course and Exam before uploading.');
      return false;
    }
    return true;
  }

  private resetBarcodeFile(): void {
    this.barcodeFile = null;
    this.barcodeFileName = '';
    const input = document.getElementById('barcodeFileInput') as HTMLInputElement;
    if (input) input.value = '';
  }

  private resetEvaluationFile(): void {
    this.evaluationFile = null;
    this.evaluationFileName = '';
    const input = document.getElementById('evaluationFileInput') as HTMLInputElement;
    if (input) input.value = '';
  }

  resetBarcodeUpload(): void {
    this.resetBarcodeFile();
    this.barcodeProgress = 0;
    this.barcodeCurrentRecord = 0;
    this.barcodeTotalRecords = 0;
    this.barcodeSuccessCount = 0;
    this.barcodeFailCount = 0;
    this.barcodeEstimatedTime = '';
    this.barcodeFailedRecords = [];
    this.barcodeShowDownload = false;
    this.barcodeLogs = [];
    this.barcodeLogsSuccessCount = 0;
    this.barcodeLogsFailCount = 0;
  }

  resetEvaluationUpload(): void {
    this.resetEvaluationFile();
    this.evaluationProgress = 0;
    this.evaluationCurrentRecord = 0;
    this.evaluationTotalRecords = 0;
    this.evaluationSuccessCount = 0;
    this.evaluationFailCount = 0;
    this.evaluationEstimatedTime = '';
    this.evaluationFailedRecords = [];
    this.evaluationShowDownload = false;
    this.evaluationLogs = [];
    this.evaluationLogsSuccessCount = 0;
    this.evaluationLogsFailCount = 0;
  }

  private showSuccess(message: string): void {
    const data: any = { message, title: 'Information', icon: 'fas fa-check' };
    this.dialogRef = this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, data);
  }

  private showError(message: string): void {
    const data: any = { message, title: 'Error', icon: 'fas fa-times' };
    this.dialogRef = this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, data);
  }

  ngOnDestroy(): void {
    this.releaseWakeLock();
  }
}